import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { AwCtx } from './ctx';
import RelatedDocsBtn from './relatedDocs';
import RowMenu, { type RowMenuEntry } from './RowMenu';
import { amt, pillAmber, pillBlue, pillGreen, pillGrey, pillRed, td } from './ui';

// ── The two cells every document list shares (owner 2026-11-02, "Standardized Document Table Layout") ───────────────────
// Seven columns on every list: No. | Date | Customer | Total | Currency | Status | Actions, with Supplier or Payee for Customer. The owner's
// ruling on what used to have a column of its own: Paid and Outstanding are small text in the Status cell, the supplier's document number
// is small text under the number, and exports keep every column. So what a row has beyond the seven lands in one of these two cells, and
// nowhere else: a list never grows an eighth column to say something.

/** Thumb-sized under a coarse pointer, with nothing moving on the page (styles/global.css). */
export const TAP_TARGET = 'aw-tap-target';
const small: React.CSSProperties = { fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)', whiteSpace: 'nowrap', marginTop: 2 };
/** A row struck through, the project's convention for what stands voided: the row stays. */
export const STRUCK: React.CSSProperties = { color: 'var(--text-tertiary)', textDecoration: 'line-through' };
/** A row greyed and not struck: a Rejected quotation, which stands and may be changed back (owner 2026-11-02, the requirement's screenshot). */
export const GREYED: React.CSSProperties = { color: 'var(--text-tertiary)' };

/** The number, the related-documents icon beside it, and optional small text under it. `lead`: a control in front of the number (the
 *  register's tick for "Record one payment"). A document with no number passes <NoNumber />, and its small text is then what names it. */
export function NoCell({ no, sub, lead, tail, rel, strike, grey }: {
  no: React.ReactNode; sub?: React.ReactNode; lead?: React.ReactNode; tail?: React.ReactNode; strike?: boolean; grey?: boolean;
  rel?: { ctx: AwCtx; kind: string; id: string; docNo: string; count?: number };
}) {
  return (
    <td data-no-cell="" style={{ ...td, whiteSpace: 'nowrap', ...(strike ? STRUCK : grey ? GREYED : {}) }}>
      {lead}
      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{no}</span>
      {rel && <RelatedDocsBtn ctx={rel.ctx} kind={rel.kind} id={rel.id} docNo={rel.docNo} count={rel.count} />}
      {tail}
      {!!sub && <div data-no-sub="" style={small}>{sub}</div>}
    </td>
  );
}
/** What the number column prints for a document that has none. Older rows get none (the owner's ruling: no backfill). */
export const NoNumber = ({ title }: { title?: string }) => <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }} title={title}>—</span>;

/**
 * The status, and under it what moved here from its own column. `menu` makes the pill a real dropdown.
 *
 * ⛔ A DROPDOWN ONLY WHERE A DOOR ALREADY EXISTS. An order's, a scan's, a record's, a claim's and a supplier's note's status has one, each
 * through the door and the dialog it always had, and the list hands them over as `menu`. A sales document's Paid or Partial is DERIVED
 * (db/index.ts "DERIVED, not user-set"), so no person picks one, but Void and Delete are the doors a person ends one with, and those are
 * what its dropdown holds (panes/Documents.tsx statusMenu; a quotation's holds the status a person does choose, stageMenu). A plain pill says
 * there is no door: a row that has ended, a payment here, because Expenses › Payments is read-only by ruling, and a seat that may not write.
 * Whatever the dropdown offers it reads first, each time it opens (RowMenu's `read`), because a click the door refuses pages the admins.
 */
export function StatusCell<V = unknown>({ word, tone, value, title, menu, beside, attrs, children }: {
  word: React.ReactNode; tone: React.CSSProperties;
  /** The stored value, on the DOM for what reads it: it may not be the word on screen (an issued bill is stored 'draft'). A quotation's: what it shows, of the status a person chooses. */
  value?: string;
  title?: string;
  menu?: { items: (verdict: V | null, readError: string) => RowMenuEntry[]; read?: () => Promise<V>; busy?: boolean };
  /** A second pill in the same cell: the register's payment status, next to its document status. */
  beside?: React.ReactNode;
  attrs?: Record<string, string>;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const on = { 'data-status-value': value ?? '', ...(attrs || {}) };
  return (
    <td data-status-cell="" style={td}>
      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {menu
          ? <RowMenu<V> items={menu.items} read={menu.read} label={t('aw_list_status_title')} trigger={{
              // TAP_TARGET: a pill this small sits in a row that opens on a click.
              attrs: { 'data-status-menu': '', className: TAP_TARGET, ...on }, title,
              style: { ...tone, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 3 },
              content: <>{word}<i className={menu.busy ? 'ti ti-loader' : 'ti ti-chevron-down'} style={{ fontSize: 10 }} aria-hidden /></>,
            }} />
          : <span style={tone} title={title} {...on}>{word}</span>}
        {beside}
      </span>
      {children}
    </td>
  );
}

/** One small figure in a Status cell: the word and the amount. `owed`: what is still owing, always in red. */
export const FigureLine = ({ name, label, value, owed }: { name: string; label: string; value: unknown; owed?: boolean }) => (
  <div data-small={name} style={{ ...small, ...(owed ? { color: '#B42318', fontWeight: 700 } : {}) }}>{label}: {amt(value)}</div>
);
/** One small sentence in a Status cell. */
export const SmallLine = ({ name, tone, children }: { name: string; tone?: string; children: React.ReactNode }) => (
  <div data-small={name} style={{ ...small, whiteSpace: 'normal', minWidth: 120, ...(tone ? { color: tone } : {}) }}>{children}</div>
);

// ── A sales document's status, in the words the client's portal prints (ClientPortal/index.tsx `stat`) ─────────────────────────────────
// ⛔ THE STORED WORD IS NOT THE STATUS ON THE PAPER. `draft` on an invoice means "issued, nothing received yet" (db/index.ts), so printing
// it told the reader a booked tax invoice was a draft: there is no real Draft state (the owner's ruling). A person reads what the portal
// says of the same row: VOID, Paid, Partial; Unpaid on a bill; Open on a quotation or a note, which nothing is owed against as such; and a
// receipt, being the payment, is Paid. Only the words change: the stored value and what exports print stay as they are, and the column
// sorts by these words' order (acctWorkspace.ts DOC_SORTS). Two departures from the portal's words, and only here (the portal's windows are
// locked): a PROFORMA reads Open, not Unpaid, because a proforma is not a receivable (owner 2026-10-02, the AR card's rule) and its own hover
// says nothing is owed; and VOID is the workspace's key, because the portal's is English in all three languages, and in one panel of related
// documents a voided invoice would read VOID beside a voided order's 已作废. A quotation's status is the one a person chooses (owner 2026-11-02,
// services/quotationStage.ts), which the server sends where it knows it: Awaiting, Issued, Rejected, and it is what its export prints too.
const SALES_KIND = new Set(['quotation', 'invoice', 'proforma', 'tax_invoice', 'tax_invoice_receipt', 'credit_note', 'debit_note', 'note', 'receipt']);
const BILL_KIND = new Set(['invoice', 'tax_invoice', 'tax_invoice_receipt']);
// A quotation's status, the one a person chooses (owner 2026-11-02, services/quotationStage.ts): what the server sends it as, where it knows it.
const OFFER_KEY: Record<string, string> = { awaiting: 'aw_offer_awaiting', issued: 'aw_offer_issued', rejected: 'aw_offer_rejected' };
/** The key of a sales document's status word, by its family key or stored doc_type; '' for anything that is not one. */
export const salesStatusKey = (kind: string, status: string): string => {
  if (!SALES_KIND.has(kind)) return '';
  if (status === 'void') return 'aw_rel_st_void';
  if (kind === 'quotation' && Object.prototype.hasOwnProperty.call(OFFER_KEY, status)) return OFFER_KEY[status];
  if (kind === 'receipt' || status === 'paid') return 'p_doc_status_paid';
  if (status === 'partial') return 'p_doc_status_partial';
  return BILL_KIND.has(kind) ? 'p_doc_status_unpaid' : 'p_doc_status_open';
};
export const salesStatus = (t: TFunction<'translation', undefined>, kind: string, status: string): { word: string; tone: React.CSSProperties } => {
  const key = salesStatusKey(kind, status);
  return { word: key ? t(key) : status, tone: key === 'aw_rel_st_void' || key === 'aw_offer_rejected' ? pillRed : key === 'aw_offer_issued' ? pillBlue : key === 'p_doc_status_paid' ? pillGreen : key === 'p_doc_status_partial' ? pillAmber : pillGrey };
};
